import { gql } from '@apollo/client';
import { useCampaignQueryContext } from 'modules/campaignIndex/campaignFolders/contexts/CampaignQueryContext';
import {
  StyledContentHeading,
  Details,
  StyledNBACampaignsListItem,
  StyledSecondaryCaption,
  StyledNBACampaignsList,
  StyledTruncatedText,
  StyledCampaignPreviewThumbnail,
  StyledLink,
  StyledButtonContainer,
} from 'modules/ai/nba/components/campaigns/NBACampaignsSlideOver.styles';
import { CampaignPreviewThumbnail } from 'modules/campaigns/lib/components/CampaignPreviewThumbnail';
import { GraphQLCampaign } from 'modules/campaignIndex/domainTypes';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { useTemplatesPreviewQuery, useCampaignsDataQuery } from 'modules/ai/hooks';
import { messageMediumMap } from 'modules/campaignIndex/constants';
import { Analytics16 } from 'ui/icons/aurora/16px/Analytics16';
import { getCampaignLink } from 'modules/campaignIndex/utils';
import { ErrorStatePageError, AlertsZeroState } from 'ui/illustrations';
import { PlaceholderState } from 'ui/indexPages/PlaceholderState';
import { sortByDescendingEndDate } from 'modules/ai/nba/utils/nbaCampaignsSlideover';
import { NBACampaignSlideOverBodySkeleton } from 'modules/ai/nba/components/campaigns/NBACampaignSlideOverBodySkeleton';

export const fetchCampaignsForNBA = gql`
  query getCampaigns($ids: [Long!]) {
    campaigns(ids: $ids) {
      results {
        id
        messageMedium
        name
        templateId
        endedAt
      }
    }
  }
`;

export const NBACampaignsSlideOverBodyContent = () => {
  const { nbaEnabledCampaignIds } = useCampaignQueryContext();

  const {
    isLoading: isFetchCampaignsDataLoading,
    isError: isFetchCampaignsDataError,
    data: campaignData,
  } = useCampaignsDataQuery<GraphQLCampaign>(
    fetchCampaignsForNBA,
    false,
    false,
    nbaEnabledCampaignIds,
  );

  const parsedTemplateIds = campaignData
    ? campaignData?.map((campaign) => campaign?.templateId)
    : [];

  const {
    isLoading: isTemplatesPreviewLoading,
    isError: previewThumbnailsByIdError,
    data: previewThumbnailsByIdData,
  } = useTemplatesPreviewQuery(
    campaignData !== undefined,
    parsedTemplateIds,
    'NBACampaignsTemplatesPreviews',
  );

  if (isTemplatesPreviewLoading || isFetchCampaignsDataLoading) {
    return <NBACampaignSlideOverBodySkeleton />;
  }

  if (previewThumbnailsByIdError || isFetchCampaignsDataError || !campaignData) {
    return (
      <PlaceholderState
        data-test="nba-eligible-slideover-content-error"
        Illustration={ErrorStatePageError}
        title="We’ve hit a snag"
      >
        Sorry, we’re having trouble loading campaign suggestions. Come back again later or try
        refreshing the page.
      </PlaceholderState>
    );
  }

  if (campaignData?.length === 0) {
    return (
      <PlaceholderState
        data-test="nba-eligible-slideover-no-data"
        Illustration={AlertsZeroState}
        title="There's nothing here"
      >
        Recent campaigns seem to be looking good! There aren’t any suggestions at this time.
      </PlaceholderState>
    );
  }

  const campaignsSortedByEndDate = sortByDescendingEndDate(campaignData);

  return (
    <>
      <StyledContentHeading>Retarget your unengaged users</StyledContentHeading>
      <StyledNBACampaignsList data-test="nba-eligible-campaigns-list-container">
        {campaignsSortedByEndDate.map((campaign) => {
          const campaignLink = getCampaignLink(campaign);
          return (
            <StyledNBACampaignsListItem key={campaign.id}>
              <StyledCampaignPreviewThumbnail>
                <CampaignPreviewThumbnail
                  campaign={campaign}
                  previewThumbnail={previewThumbnailsByIdData[campaign.templateId]}
                />
              </StyledCampaignPreviewThumbnail>
              <StyledLink to={campaignLink}>
                <StyledTruncatedText>{campaign.name}</StyledTruncatedText>
                <Details>
                  <StyledSecondaryCaption>
                    {messageMediumMap[campaign.messageMedium]}
                  </StyledSecondaryCaption>
                  <StyledSecondaryCaption data-test="campaign-id">{`ID: ${campaign.id}`}</StyledSecondaryCaption>
                </Details>
              </StyledLink>
              <StyledButtonContainer>
                <StyledLink to={campaignLink}>
                  <IconButton size={BUTTON_SIZES.MEDIUM} variant={BUTTON_VARIANTS.SECONDARY}>
                    <Analytics16 />
                  </IconButton>
                </StyledLink>
              </StyledButtonContainer>
            </StyledNBACampaignsListItem>
          );
        })}
      </StyledNBACampaignsList>
    </>
  );
};
